import React from 'react';
import type { FmDataDisplayRule } from '../../types/fm-data-value';
import { FmDataValue } from './FmDataValue';
import { FmKeyFigures, type FmKeyFigure } from './FmKeyFigures';
import './ui.css';
import './FmDataBlock.css';

export interface FmDataBlockItem {
  id: string;
  label: string;
  value?: number | string | null;
  unit?: string;
  kind?: FmDataDisplayRule['kind'];
  precision?: number;
  directional?: boolean;
  helperText?: string;
  testId?: string;
}

export interface FmDataBlockProps {
  items: FmDataBlockItem[];
  /** Required unit for this data block; item.unit can override per value. */
  unit: string;
  /**
   * Human-readable caption for standalone data blocks. Optional: when the
   * callout eyebrow (item.label) already names the metric, omit the caption
   * rather than repeating the same words.
   */
  caption?: React.ReactNode;
  /** Plain-language reading of the numbers. */
  takeaway?: React.ReactNode;
  title?: React.ReactNode;
  valueKind?: FmDataDisplayRule['kind'];
  locale?: string;
  unavailableLabel?: string;
  variant?: 'auto' | 'callout' | 'stat-row';
  /**
   * Visual surface for report-band data. `plain` keeps the historical bare
   * callout/stat rows; `chart` uses the semantic chart surface and unboxes
   * nested stat cells.
   */
  surface?: 'plain' | 'chart';
  /** ChartBlock sets this false so context remains outside the figure frame. */
  showContext?: boolean;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

const DEFAULT_VALUE_KIND: FmDataDisplayRule['kind'] = 'number';

function isUnavailable(value: FmDataBlockItem['value']): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'number' && !Number.isFinite(value)) return true;
  return false;
}

function defaultPrecision(kind: FmDataDisplayRule['kind']): number | undefined {
  if (kind === 'percent' || kind === 'volume') return 1;
  if (kind === 'area') return 2;
  if (kind === 'currency' || kind === 'number') return 0;
  return undefined;
}

function formatStatValue(
  item: FmDataBlockItem,
  fallbackKind: FmDataDisplayRule['kind'],
  locale: string,
): string | null {
  if (item.kind === 'unavailable' || isUnavailable(item.value)) return null;
  const kind = item.kind ?? fallbackKind;
  const value = item.value as number | string;

  if (kind === 'text' || kind === 'date') return String(value);

  const numericValue = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numericValue)) return null;

  const precision = item.precision ?? defaultPrecision(kind);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(numericValue);
}

function toStatItems(
  items: FmDataBlockItem[],
  unit: string,
  valueKind: FmDataDisplayRule['kind'],
  locale: string,
  unavailableLabel: string | undefined,
): FmKeyFigure[] {
  return items.map((item) => {
    const base = {
      id: item.id,
      label: item.label,
      unit: item.unit ?? unit,
      footnote: item.helperText,
      testId: item.testId,
    };
    const value = formatStatValue(item, valueKind, locale);
    return value === null
      ? { ...base, value: null, unavailableReason: unavailableLabel }
      : { ...base, value };
  });
}

function renderCallout(
  item: FmDataBlockItem | undefined,
  unit: string,
  valueKind: FmDataDisplayRule['kind'],
  locale: string,
  unavailableLabel: string | undefined,
): React.ReactElement {
  const rule: FmDataDisplayRule = {
    kind: item?.kind ?? valueKind,
    value: item?.value,
    unit: item?.unit ?? unit,
    precision: item?.precision,
    directional: item?.directional,
  };

  return (
    <div className="fm-data-block__callout" data-testid="fm-data-block-callout">
      <span className="fm-data-block__label">{item?.label ?? 'Unavailable'}</span>
      <FmDataValue
        rule={rule}
        locale={locale}
        unavailableLabel={unavailableLabel}
        data-testid={item?.testId ?? 'fm-data-block-value'}
      />
      {item?.helperText ? (
        <span className="fm-data-block__helper">{item.helperText}</span>
      ) : null}
    </div>
  );
}


// Surface mode to class mapping — reduces complexity
const SURFACE_CLASS_MAP = { chart: 'fm-data-block--surface-chart', plain: '' };
const SURFACE_STAT_ROW_MAP = { chart: 'chart' as const, plain: 'card' as const };

/**
 * Context rail: plain-language takeaway + caption. Rendered only when there is
 * something to say — the orphan full-width unit glyph was removed since the
 * value already carries its unit inline.
 */
function renderContext(
  takeaway: React.ReactNode,
  caption: React.ReactNode,
): React.ReactElement | null {
  if (!takeaway && !caption) return null;
  return (
    <div className="fm-data-block__context">
      {takeaway ? <div className="fm-data-block__takeaway">{takeaway}</div> : null}
      {caption ? <div className="fm-data-block__caption">{caption}</div> : null}
    </div>
  );
}

export const FmDataBlock: React.FC<FmDataBlockProps> = ({
  items,
  unit,
  caption,
  takeaway,
  title,
  valueKind = DEFAULT_VALUE_KIND,
  locale = 'fi-FI',
  unavailableLabel,
  variant = 'auto',
  surface = 'plain',
  showContext = true,
  className,
  'data-testid': dataTestId,
}) => {
  const displayMode = variant === 'auto'
    ? (items.length <= 1 ? 'callout' : 'stat-row')
    : variant;

  return (
    <section
      className={cx(
        'fm-data-block',
        `fm-data-block--${displayMode}`,
        SURFACE_CLASS_MAP[surface],
        !showContext && 'fm-data-block--contextless',
        className,
      )}
      data-testid={dataTestId ?? 'fm-data-block'}
    >
      {title ? <div className="fm-data-block__title">{title}</div> : null}
      {displayMode === 'callout' ? (
        renderCallout(items[0], unit, valueKind, locale, unavailableLabel)
      ) : (
        <FmKeyFigures
          items={toStatItems(items, unit, valueKind, locale, unavailableLabel)}
          className="fm-data-block__stats"
          surface={SURFACE_STAT_ROW_MAP[surface]}
        />
      )}
      {showContext ? renderContext(takeaway, caption) : null}
    </section>
  );
};

export default FmDataBlock;
